import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'
import { AppState } from 'src/state/store'

export enum PaylinkaStepEnum {
  IdentityVerification = 1,
  Address = 2,
  AboutYou = 3,
  AccountUse = 4,
  SourceOfFunds = 5,
  Declarations = 6,
  Completed = 10,
}

// Lightweight reference to the online identity-verification session the user is
// going through. Provider-agnostic: the backend abstracts the actual provider.
export interface VerificationSession {
  sessionId: string
  status: string
}

export interface PaylinkaVerificationState {
  currentStep: PaylinkaStepEnum
  availableStep: PaylinkaStepEnum
  isCompleted: boolean
  formValues: Record<string, unknown>
  verificationSession: VerificationSession | null
  // Id of the user this in-progress questionnaire belongs to. Logout no longer
  // wipes the slice (so the same user can resume after logging back in), so the
  // owner is what guards against a different user on the same browser inheriting
  // these answers. See `claimPaylinkaOwner`.
  ownerId: number | null
}

export const initialPaylinkaVerificationState: PaylinkaVerificationState = {
  currentStep: PaylinkaStepEnum.IdentityVerification,
  availableStep: PaylinkaStepEnum.IdentityVerification,
  isCompleted: false,
  formValues: {},
  verificationSession: null,
  ownerId: null,
}

const hydrate = createAction<AppState>(HYDRATE)

const paylinkaVerificationSlice = createSlice({
  name: 'paylinkaVerification',
  initialState: initialPaylinkaVerificationState,
  reducers: {
    changeCurrentStep(state, action: PayloadAction<PaylinkaStepEnum>) {
      state.currentStep = action.payload
    },
    changeAvailableStep(state, action: PayloadAction<PaylinkaStepEnum>) {
      state.availableStep = action.payload
    },
    progressToStep(state, action: PayloadAction<PaylinkaStepEnum>) {
      const nextStep = action.payload

      state.currentStep = nextStep

      if (nextStep > state.availableStep) {
        state.availableStep = nextStep
      }
    },
    addFormValues(state, action: PayloadAction<Record<string, unknown>>) {
      state.formValues = { ...state.formValues, ...action.payload }
    },
    setVerificationSession(
      state,
      action: PayloadAction<VerificationSession | null>
    ) {
      state.verificationSession = action.payload
    },
    completePaylinkaVerification(state) {
      state.isCompleted = true
      state.currentStep = PaylinkaStepEnum.Completed
      state.availableStep = PaylinkaStepEnum.Completed
    },
    // Claim the slice for the logged-in user on every login. The same owner
    // keeps their in-progress answers (resume after re-login); a different or
    // not-yet-known owner gets a clean questionnaire, so one user never inherits
    // another user's KYC answers on a shared browser.
    claimPaylinkaOwner(state, action: PayloadAction<number>) {
      if (state.ownerId !== action.payload) {
        return { ...initialPaylinkaVerificationState, ownerId: action.payload }
      }
    },
    flushPaylinka() {
      return initialPaylinkaVerificationState
    },
  },
  extraReducers: builder => {
    builder.addCase(hydrate, state => state)
  },
})

const { actions, reducer } = paylinkaVerificationSlice

export const {
  changeCurrentStep,
  changeAvailableStep,
  progressToStep,
  addFormValues,
  setVerificationSession,
  completePaylinkaVerification,
  claimPaylinkaOwner,
  flushPaylinka,
} = actions

export default reducer
